import React, { useState, useRef, useEffect, ReactNode } from "react";
import { useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";

import SwitchComponent from "../form-item/SwitchComponent";
import CloseButton from "../button/CloseButton";
import type { ViewMode } from "../../types/home";

/** バー（セグメント行）の高さ。フロート時のコンテンツ側インセットに使う */
export const TOP_APP_BAR_HOME_HEIGHT = 48;

/** GlobalHeader の高さ（GlobalHeader.jsx の Header と同値） */
const GLOBAL_HEADER_HEIGHT = 64;

/** ProjectBanner の高さ（ProjectBanner.tsx / --project-banner-height と同値） */
const PROJECT_BANNER_HEIGHT = 75;

/**
 * sticky の top（px）。GlobalHeader の表示状態に追従し（表示中 64px /
 * 下スクロールで退避中 0px）、プロジェクト内では固定のバナーの下に揃える。
 */
const useStickyTop = (belowProjectBanner: boolean, offset = 0): number => {
    const scrollState = useSelector((state: { layout: string }) => state.layout);
    const headerTop = scrollState === "down" ? 0 : GLOBAL_HEADER_HEIGHT;
    return headerTop + (belowProjectBanner ? PROJECT_BANNER_HEIGHT : 0) + offset;
};

/**
 * ウィンドウスクロールのページでは sticky で画面上部にピン留めし、
 * コンテンツがバーの下をくぐって流れる。
 */
const TopAppBarHomeStyle = styled.div.attrs({
    className: "u-row-center"
})<{ $top: number }>`
    width: 100%;
    height: ${TOP_APP_BAR_HOME_HEIGHT}px;
    padding: 0px 16px 0px 16px;
    gap: 8px;

    position: sticky;
    top: ${({ $top }) => $top}px;
    transition: top 0.2s;

    z-index: 4;
`;

/**
 * 内部スクロールコンテナを持つページ用のフロート層。バーをこの中に入れると
 * フロー上の高さを持たなくなり、スクロールコンテンツはバーの下をくぐって
 * 上端（プロジェクトバナー直下など）まで描画される。
 * 配置基準となる祖先に position: relative が必要。コンテンツ側には
 * TOP_APP_BAR_HOME_HEIGHT 分の上端インセットを確保すること。
 */
export const TopAppBarFloat = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;

  /* フロート内では sticky を無効化する。sticky のままだと最近傍の
     スクロールポート（overflow を持つ祖先）基準の top オフセットが効き、
     フロート内に複数行があるときバーが押し下げられてしまう */
  ${TopAppBarHomeStyle} {
    position: static;
  }
`;

const TopAppBarSubRowStyle = styled.div<{ $top: number }>`
    position: sticky;
    top: ${({ $top }) => $top}px;
    transition: top 0.2s;
    /* バー（z4）の影の下、表のリサイズハンドル（z2）より上 */
    z-index: 3;
`;

interface TopAppBarSubRowProps {
    /** TopAppBarHome 側の belowProjectBanner と揃える */
    belowProjectBanner?: boolean;
    /**
     * 内部スクロールコンテナの中に置く場合（TopAppBarHome がコンテナの外の
     * 上にある）。ピン留め位置をコンテナ上端にする
     */
    insideScrollContainer?: boolean;
    children: ReactNode;
}

/**
 * TopAppBarHome の直下に続けてピン留めする行（タグの FilterChipBar 等）。
 * sticky の可動範囲は親要素内のため、親はスクロールするコンテンツ全体を
 * 含んでいること。
 */
export const TopAppBarSubRow: React.FC<TopAppBarSubRowProps> = ({
    belowProjectBanner = false,
    insideScrollContainer = false,
    children,
}) => {
    const windowTop = useStickyTop(belowProjectBanner, TOP_APP_BAR_HOME_HEIGHT);
    const top = insideScrollContainer ? 0 : windowTop;
    return <TopAppBarSubRowStyle $top={top}>{children}</TopAppBarSubRowStyle>;
};

interface SwitchOption {
    label?: string;
    icon?: string;
    value: ViewMode;
}

interface TopAppBarHomeProps {
    onChange: (value: ViewMode) => void;
    options?: SwitchOption[];
    defaultValue?: ViewMode;
    value?: ViewMode;
    showSearchInput?: boolean;
    onSearchChange?: (searchText: string) => void;
    searchPlaceholder?: string;
    /**
     * セグメント左端アイコン（右端の検索アイコンと対）。
     * - `schedule`: プロジェクト内 Home のショット表
     * - `files`: トークルームのファイル一覧（ストレージビュー）
     */
    leftIcon?: "schedule" | "files";
    /**
     * 行の左端（セグメントコントロールの外側）に置く要素。
     */
    leftActions?: ReactNode;
    /**
     * 行の右端（セグメントコントロールの外側）に置く要素。
     * プロジェクト内タブ共通のプロジェクトメニュー（ProjectMenu）用。
     */
    rightActions?: ReactNode;
    /**
     * プロジェクト内（固定の ProjectBanner がある画面）で、バナーの下に
     * ピン留めする。未指定だと GlobalHeader 直下で止まりバナーに隠れる。
     */
    belowProjectBanner?: boolean;
}


const FilterWrapper = styled.div`
    display: flex;
    align-items: center;
    width: clamp(280px, 100% - 32px, 520px);
    /* CommentField（CommentFieldContainer）/ BottomAppBar と同一のグラス。
       下をくぐるコンテンツが同じ質感で透ける */
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(13px);
    -webkit-backdrop-filter: blur(13px);
    overflow: hidden;
    /* leftActions がある場合も残り空間の中央に寄せる。狭幅では flex 縮小で
     * 左端のアイコンと重ならない（overflow: hidden により min-width が 0 扱い） */
    margin-inline: auto;
    min-width: 0;
`;

const TopBarSwitch = styled(SwitchComponent)`
    width: 100%;
    overflow: hidden;
    border: none;
    box-shadow: none;
    border-radius: 0;
    /* FilterWrapper 側のグラスを見せるため、スイッチ自体の面は持たない */
    background-color: transparent;
`;

// --- Search overlay components ---

const fieldSlideUp = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
`;

const SearchOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    pointer-events: none;
`;

const SearchFieldContainer = styled.div`
    border-radius: 6px;
    border: 1px solid #f0f0f0;
    max-width: 740px;
    width: calc(100% - 32px);
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(13px);
    -webkit-backdrop-filter: blur(13px);
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: auto;
    animation: ${fieldSlideUp} 0.25s ease;
`;

const SearchIcon = styled.span.attrs({
    className: "material-symbols-rounded"
})`
    font-size: 20px;
    color: var(--soremo-light-gray);
    flex-shrink: 0;
`;

const SearchOverlayInput = styled.input`
    width: 100%;
    min-height: 24px;
    padding: 0;
    border: none;
    background-color: transparent;
    outline: none;
    font-size: 16px;
    line-height: 1.5;

    &::placeholder {
        color: var(--soremo-light-gray);
    }
`;

// デフォルトのオプション（Homeページ用）
const defaultOptions: SwitchOption[] = [
    { label: "進行中", value: "in_progress" },
    { label: "完了", value: "completed" },
];

const TopAppBarHome: React.FC<TopAppBarHomeProps> = ({
    onChange,
    options = defaultOptions,
    defaultValue = "in_progress",
    value,
    showSearchInput = false,
    onSearchChange,
    searchPlaceholder = "検索...",
    leftIcon,
    leftActions,
    rightActions,
    belowProjectBanner = false,
}) => {
    const initialValue = value ?? defaultValue;
    const [currentMode, setCurrentMode] = useState<ViewMode>(initialValue);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [switchValue, setSwitchValue] = useState<ViewMode>(initialValue);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const stickyTop = useStickyTop(belowProjectBanner);

    useEffect(() => {
        if (value !== undefined && value !== "search") {
            setCurrentMode(value);
            setSwitchValue(value);
        }
    }, [value]);

    useEffect(() => {
        if (isSearchMode && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isSearchMode]);

    // styled(SwitchComponent) はジェネリクスを保持できず value が string | number | null
    // で渡ってくるため、ここで ViewMode に絞り込む（required のため null は実際には来ない）
    const handleModeChange = (newValue: string | number | null) => {
        if (typeof newValue !== "string") return;
        const mode = newValue as ViewMode;
        if (mode === "search") {
            setIsSearchMode(true);
        } else {
            setSwitchValue(mode);
            setCurrentMode(mode);
            onChange(mode);
        }
    };

    const handleCloseSearch = () => {
        setIsSearchMode(false);
        setSearchText("");
        onSearchChange?.("");
        setSwitchValue(currentMode);
        onChange(currentMode);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;
        setSearchText(text);
        onSearchChange?.(text);
        if (text.trim()) {
            onChange("search");
        } else {
            onChange(currentMode);
        }
    };

    // 左端アイコン（schedule / files）と右端の検索アイコンを対になる配置にする
    const LEFT_ICON_NAME_MAP: Record<NonNullable<TopAppBarHomeProps["leftIcon"]>, string> = {
        schedule: "event_note",
        files: "home_storage",
    };
    const optionsWithIcons = [
        ...(leftIcon
            ? [{ icon: LEFT_ICON_NAME_MAP[leftIcon], value: leftIcon as ViewMode }]
            : []),
        ...options.filter(
            (opt) =>
                opt.value !== "search" &&
                opt.value !== "schedule" &&
                opt.value !== "files",
        ),
        ...(showSearchInput ? [{ icon: "search", value: "search" as ViewMode }] : []),
    ];

    return (
        <TopAppBarHomeStyle $top={stickyTop}>
            {leftActions}
            <FilterWrapper>
                <TopBarSwitch
                    defaultValue={defaultValue}
                    value={switchValue}
                    options={optionsWithIcons}
                    onChange={handleModeChange}
                />
            </FilterWrapper>
            {rightActions}

            {showSearchInput && isSearchMode && (
                <SearchOverlay>
                    <SearchFieldContainer>
                        <SearchIcon>search</SearchIcon>
                        <SearchOverlayInput
                            ref={searchInputRef}
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchText}
                            onChange={handleSearchChange}
                        />
                        <CloseButton onClick={handleCloseSearch} ariaLabel="検索を閉じる" />
                    </SearchFieldContainer>
                </SearchOverlay>
            )}
        </TopAppBarHomeStyle>
    );
};

export default TopAppBarHome;
